import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/lib/utils";

/**
 * Tooltip — accessible hover/focus tooltip built on Radix.
 *
 * Wrap your app once in <TooltipProvider>, then use <Tooltip> per element.
 * For one-offs, the convenience `<TooltipShort>` wraps everything inline.
 *
 * @example
 *   <TooltipProvider delayDuration={300}>
 *     <Tooltip>
 *       <TooltipTrigger asChild><Button>Hover me</Button></TooltipTrigger>
 *       <TooltipContent>Tooltip text</TooltipContent>
 *     </Tooltip>
 *   </TooltipProvider>
 *
 *   // or, when no provider is set up at app level:
 *   <TooltipShort label="Einchecken">
 *     <Button>...</Button>
 *   </TooltipShort>
 */
const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef(
  ({ className, sideOffset = 6, ...props }, ref) => (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-[9999] px-[0.7rem] py-[0.35rem] rounded-[0.4rem]",
          "bg-[#18181b] text-white text-[1.1rem] font-medium whitespace-nowrap",
          "shadow-md pointer-events-none",
          "data-[state=delayed-open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0",
          className,
        )}
        {...props}
      />
    </TooltipPrimitive.Portal>
  ),
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/**
 * Convenience wrapper — pass children + label and you get the full
 * Provider/Root/Trigger/Content stack inline.
 */
const TooltipShort = ({ label, children, side, sideOffset, delayDuration = 200 }) => (
  <TooltipProvider delayDuration={delayDuration}>
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} sideOffset={sideOffset}>
        {label}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent, TooltipShort };
